/**
 * Colour-space conversions for the colour tools.
 *
 * Everything starts from sRGB with channels 0–255 and an alpha of 0–1, because that is
 * what a web page ends up painting. The formulas are the ones in CSS Color Module
 * Level 4 (https://www.w3.org/TR/css-color-4/): the sRGB transfer function with its
 * 0.04045 threshold, the HSL/HWB algorithms of §7 and §8, and Björn Ottosson's OKLab for
 * OKLCH. HSV and CMYK are not CSS colour functions; they are the textbook conversions.
 *
 * CMYK here is the naive device conversion (K = 1 − max(R, G, B)). It involves no ink,
 * paper or ICC profile, so a printer will not reproduce the screen colour from it.
 *
 * Output strings use CSS syntax, so decimals are always `.` whatever the page's locale.
 */

export interface Rgb {
  /** 0–255; fractional when the colour came from a non-sRGB-integer source. */
  r: number;
  g: number;
  b: number;
  /** 0–1. */
  a: number;
}

export interface Hsl {
  /** Degrees, 0 ≤ h < 360. */
  h: number;
  /** Percent, 0–100. */
  s: number;
  l: number;
}

export interface Hsv {
  h: number;
  s: number;
  v: number;
}

export interface Hwb {
  h: number;
  /** Whiteness and blackness, percent. */
  w: number;
  b: number;
}

export interface Cmyk {
  /** Percent, 0–100. */
  c: number;
  m: number;
  y: number;
  k: number;
}

export interface Oklch {
  /** Lightness 0–1. */
  l: number;
  c: number;
  /** Degrees. */
  h: number;
}

/** Below this chroma a colour is grey and its hue is meaningless (CSS: "powerless"). */
const ACHROMATIC_CHROMA = 0.0004;

export function clamp(value: number, low: number, high: number): number {
  return Math.min(high, Math.max(low, value));
}

export function mod360(degrees: number): number {
  return ((degrees % 360) + 360) % 360;
}

/* ------------------------------------------------------------ transfer function */

/** sRGB-encoded 0–1 to linear light (CSS Color 4 §10.3, threshold 0.04045). */
export function srgbToLinear(channel: number): number {
  const sign = channel < 0 ? -1 : 1;
  const abs = Math.abs(channel);
  return abs <= 0.040_45
    ? channel / 12.92
    : sign * ((abs + 0.055) / 1.055) ** 2.4;
}

/** Linear light to sRGB-encoded 0–1 (the inverse, threshold 0.0031308). */
export function linearToSrgb(channel: number): number {
  const sign = channel < 0 ? -1 : 1;
  const abs = Math.abs(channel);
  return abs <= 0.003_130_8
    ? channel * 12.92
    : sign * (1.055 * abs ** (1 / 2.4) - 0.055);
}

/* ------------------------------------------------------------------- HSL / HSV */

function hueOf(max: number, min: number, r: number, g: number, b: number) {
  const delta = max - min;
  if (delta === 0) {
    return 0;
  }
  if (max === r) {
    return mod360(60 * (((g - b) / delta) % 6));
  }
  if (max === g) {
    return mod360(60 * ((b - r) / delta + 2));
  }
  return mod360(60 * ((r - g) / delta + 4));
}

export function rgbToHsl({ r, g, b }: Rgb): Hsl {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  const lightness = (max + min) / 2;
  const delta = max - min;
  const saturation =
    delta === 0 ? 0 : delta / (1 - Math.abs(2 * lightness - 1));
  return {
    h: hueOf(max, min, red, green, blue),
    s: clamp(saturation, 0, 1) * 100,
    l: lightness * 100,
  };
}

/** CSS Color 4 §7.1 `hsl-to-rgb`, with the result scaled to 0–255. */
export function hslToRgb({ h, s, l }: Hsl, a = 1): Rgb {
  const hue = mod360(h);
  const sat = clamp(s, 0, 100) / 100;
  const light = clamp(l, 0, 100) / 100;
  const f = (n: number) => {
    const k = (n + hue / 30) % 12;
    const amount = sat * Math.min(light, 1 - light);
    return light - amount * Math.max(-1, Math.min(k - 3, 9 - k, 1));
  };
  return { r: f(0) * 255, g: f(8) * 255, b: f(4) * 255, a };
}

export function rgbToHsv({ r, g, b }: Rgb): Hsv {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const max = Math.max(red, green, blue);
  const min = Math.min(red, green, blue);
  return {
    h: hueOf(max, min, red, green, blue),
    s: max === 0 ? 0 : ((max - min) / max) * 100,
    v: max * 100,
  };
}

export function hsvToRgb({ h, s, v }: Hsv, a = 1): Rgb {
  const hue = mod360(h);
  const sat = clamp(s, 0, 100) / 100;
  const value = clamp(v, 0, 100) / 100;
  const f = (n: number) => {
    const k = (n + hue / 60) % 6;
    return value - value * sat * Math.max(0, Math.min(k, 4 - k, 1));
  };
  return { r: f(5) * 255, g: f(3) * 255, b: f(1) * 255, a };
}

/* ------------------------------------------------------------------------ HWB */

export function rgbToHwb(rgb: Rgb): Hwb {
  const { h } = rgbToHsl(rgb);
  const red = rgb.r / 255;
  const green = rgb.g / 255;
  const blue = rgb.b / 255;
  return {
    h,
    w: Math.min(red, green, blue) * 100,
    b: (1 - Math.max(red, green, blue)) * 100,
  };
}

/** CSS Color 4 §8.1 `hwb-to-rgb`: whiteness + blackness ≥ 100% gives a grey. */
export function hwbToRgb({ h, w, b }: Hwb, a = 1): Rgb {
  const white = clamp(w, 0, 100) / 100;
  const black = clamp(b, 0, 100) / 100;
  if (white + black >= 1) {
    const grey = (white / (white + black)) * 255;
    return { r: grey, g: grey, b: grey, a };
  }
  const base = hslToRgb({ h, s: 100, l: 50 }, a);
  const scale = 1 - white - black;
  return {
    r: (base.r / 255) * scale * 255 + white * 255,
    g: (base.g / 255) * scale * 255 + white * 255,
    b: (base.b / 255) * scale * 255 + white * 255,
    a,
  };
}

/* ----------------------------------------------------------------------- CMYK */

/** Naive device CMYK: no ink, paper or ICC profile is involved. */
export function rgbToCmyk({ r, g, b }: Rgb): Cmyk {
  const red = r / 255;
  const green = g / 255;
  const blue = b / 255;
  const k = 1 - Math.max(red, green, blue);
  if (k >= 1) {
    return { c: 0, m: 0, y: 0, k: 100 };
  }
  return {
    c: ((1 - red - k) / (1 - k)) * 100,
    m: ((1 - green - k) / (1 - k)) * 100,
    y: ((1 - blue - k) / (1 - k)) * 100,
    k: k * 100,
  };
}

export function cmykToRgb({ c, m, y, k }: Cmyk, a = 1): Rgb {
  const key = 1 - clamp(k, 0, 100) / 100;
  return {
    r: 255 * (1 - clamp(c, 0, 100) / 100) * key,
    g: 255 * (1 - clamp(m, 0, 100) / 100) * key,
    b: 255 * (1 - clamp(y, 0, 100) / 100) * key,
    a,
  };
}

/* --------------------------------------------------------------------- OKLCH */

/** sRGB to OKLab (Ottosson, 2020), via linear light. */
export function rgbToOklab({ r, g, b }: Rgb): {
  l: number;
  a: number;
  b: number;
} {
  const lr = srgbToLinear(r / 255);
  const lg = srgbToLinear(g / 255);
  const lb = srgbToLinear(b / 255);
  const l = Math.cbrt(
    0.412_221_470_8 * lr + 0.536_332_536_3 * lg + 0.051_445_992_9 * lb
  );
  const m = Math.cbrt(
    0.211_903_498_2 * lr + 0.680_699_545_1 * lg + 0.107_396_956_6 * lb
  );
  const s = Math.cbrt(
    0.088_302_461_9 * lr + 0.281_718_837_6 * lg + 0.629_978_700_5 * lb
  );
  return {
    l: 0.210_454_255_3 * l + 0.793_617_785 * m - 0.004_072_046_8 * s,
    a: 1.977_998_495_1 * l - 2.428_592_205 * m + 0.450_593_709_9 * s,
    b: 0.025_904_037_1 * l + 0.782_771_766_2 * m - 0.808_675_766 * s,
  };
}

export function rgbToOklch(rgb: Rgb): Oklch {
  const lab = rgbToOklab(rgb);
  const chroma = Math.hypot(lab.a, lab.b);
  return {
    l: lab.l,
    c: chroma,
    h:
      chroma < ACHROMATIC_CHROMA
        ? 0
        : mod360((Math.atan2(lab.b, lab.a) * 180) / Math.PI),
  };
}

/** Distance between two colours in OKLab — used to name the nearest CSS colour. */
export function oklabDistance(a: Rgb, b: Rgb): number {
  const x = rgbToOklab(a);
  const y = rgbToOklab(b);
  return Math.hypot(x.l - y.l, x.a - y.a, x.b - y.b);
}

/* -------------------------------------------------------------------- output */

export function channel255(value: number): number {
  return Math.round(clamp(value, 0, 255));
}

/** Trim a number to `digits` decimals without trailing zeros, and never print "-0". */
export function trimNumber(value: number, digits: number): string {
  const rounded = Number(value.toFixed(digits));
  return String(Object.is(rounded, -0) ? 0 : rounded);
}

function alphaPart(a: number, separator: string): string {
  return a >= 1 ? "" : `${separator}${trimNumber(a, 3)}`;
}

export function toHex(rgb: Rgb): string {
  const part = (value: number) =>
    channel255(value).toString(16).padStart(2, "0");
  return `#${part(rgb.r)}${part(rgb.g)}${part(rgb.b)}`;
}

/** `#rrggbbaa`, with the alpha rounded to 8 bits as CSS does. */
export function toHex8(rgb: Rgb): string {
  const alpha = Math.round(clamp(rgb.a, 0, 1) * 255)
    .toString(16)
    .padStart(2, "0");
  return `${toHex(rgb)}${alpha}`;
}

export function toRgbString(rgb: Rgb): string {
  const body = `${channel255(rgb.r)} ${channel255(rgb.g)} ${channel255(rgb.b)}`;
  return `rgb(${body}${alphaPart(rgb.a, " / ")})`;
}

export function toHslString(rgb: Rgb): string {
  const { h, s, l } = rgbToHsl(rgb);
  return `hsl(${trimNumber(h, 1)} ${trimNumber(s, 1)}% ${trimNumber(l, 1)}%${alphaPart(rgb.a, " / ")})`;
}

export function toHwbString(rgb: Rgb): string {
  const { h, w, b } = rgbToHwb(rgb);
  return `hwb(${trimNumber(h, 1)} ${trimNumber(w, 1)}% ${trimNumber(b, 1)}%${alphaPart(rgb.a, " / ")})`;
}

export function toOklchString(rgb: Rgb): string {
  const { l, c, h } = rgbToOklch(rgb);
  return `oklch(${trimNumber(l * 100, 2)}% ${trimNumber(c, 4)} ${trimNumber(h, 2)}${alphaPart(rgb.a, " / ")})`;
}

/** Not CSS: the `hsv(…)` notation design tools use. */
export function toHsvString(rgb: Rgb): string {
  const { h, s, v } = rgbToHsv(rgb);
  return `hsv(${trimNumber(h, 1)}, ${trimNumber(s, 1)}%, ${trimNumber(v, 1)}%)`;
}

/** Not CSS (Level 4 has no cmyk()): the notation print tools use. */
export function toCmykString(rgb: Rgb): string {
  const { c, m, y, k } = rgbToCmyk(rgb);
  return `cmyk(${trimNumber(c, 1)}%, ${trimNumber(m, 1)}%, ${trimNumber(y, 1)}%, ${trimNumber(k, 1)}%)`;
}
